/**
 * Pure, DOM-free fair-share allocator for the two stacked Ask Puntt sidebar
 * sections (Projects above, Chats below).
 *
 * The rail never scrolls and never clips a row mid-height: each section shows
 * only the COMPLETE rows that fit, then its always-visible "View all" link.
 * This helper decides how many rows of each section to render given the
 * measured geometry of the rail.
 *
 * It is intentionally pure (no React, no DOM) so the max-min split can be
 * unit-tested with exact numeric assertions and no browser.
 *
 * ## Budget
 *
 * Both sections ALWAYS render their header and their "View all" link, so the
 * height left for actual rows is:
 *
 *   itemsHeight = availableHeight - (2 * headerHeight + 2 * viewAllHeight + sectionGap)
 *   budget      = floor(itemsHeight / itemHeight)   // total rows that fit
 *
 * `headerHeight` is expected to fold in any fixed per-section chrome that sits
 * above the list (e.g. the section's own vertical padding); `viewAllHeight` is
 * the fixed chrome below the list. `sectionGap` is the gap between the two
 * stacked sections. All heights are in pixels.
 *
 * ## Max-min fair split of `budget` between the two sections
 *
 * 1. If `projectsTotal + chatsTotal <= budget` → show ALL of both. Whatever
 *    height is left over is just empty space at the bottom (we never inflate a
 *    section to fill the rail).
 * 2. Otherwise the two sections compete for `budget` rows, max-min fairly:
 *    - `half = floor(budget / 2)`.
 *    - If one section needs `<= half`, it takes exactly what it needs and the
 *      OTHER section fills ALL the remaining rows (so "1 chat + 20 projects"
 *      gives chats its 1 and projects the other budget-1, and symmetrically).
 *    - If BOTH need more than `half`, split ~50/50. When `budget` is odd the
 *      extra row goes to PROJECTS (the top section) — a deterministic,
 *      documented tie-break so the result is stable across renders.
 *
 * The result never exceeds either section's total and is never negative.
 */
export interface AllocateSidebarSectionsInput {
  /** Height available to the two sections combined, in pixels. */
  availableHeight: number;
  /** Fixed per-section chrome ABOVE the list (header + section top padding). */
  headerHeight: number;
  /** Fixed per-section chrome BELOW the list (the "View all" link). */
  viewAllHeight: number;
  /** Gap between the two stacked sections, in pixels. */
  sectionGap: number;
  /** Height of a single list row, in pixels. */
  itemHeight: number;
  /** Total number of project rows available to show. */
  projectsTotal: number;
  /** Total number of chat rows available to show. */
  chatsTotal: number;
}

export interface AllocateSidebarSectionsResult {
  visibleProjects: number;
  visibleChats: number;
}

/** Clamp to a non-negative integer (defensive against NaN / fractional input). */
function toCount(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.floor(value);
}

export function allocateSidebarSections({
  availableHeight,
  headerHeight,
  viewAllHeight,
  sectionGap,
  itemHeight,
  projectsTotal,
  chatsTotal,
}: AllocateSidebarSectionsInput): AllocateSidebarSectionsResult {
  const pTotal = toCount(projectsTotal);
  const cTotal = toCount(chatsTotal);

  // Geometry not measurable yet (first paint / zero-height row). Falling back
  // to "show everything" keeps the no-scroll contract honest: we never hide a
  // row we can't prove won't fit. The measure pass corrects this once real row
  // heights are known.
  if (!Number.isFinite(itemHeight) || itemHeight <= 0) {
    return { visibleProjects: pTotal, visibleChats: cTotal };
  }

  const fixedChrome =
    2 * Math.max(0, headerHeight) +
    2 * Math.max(0, viewAllHeight) +
    Math.max(0, sectionGap);
  const itemsHeight = availableHeight - fixedChrome;
  const budget = Math.max(0, Math.floor(itemsHeight / itemHeight));

  // Everything fits — show all of both, leftover height is empty space.
  if (pTotal + cTotal <= budget) {
    return { visibleProjects: pTotal, visibleChats: cTotal };
  }

  const half = Math.floor(budget / 2);

  let visibleProjects: number;
  let visibleChats: number;

  if (pTotal <= half) {
    // Projects needs less than its fair share — it takes what it needs and
    // chats fills the rest.
    visibleProjects = pTotal;
    visibleChats = Math.min(cTotal, budget - pTotal);
  } else if (cTotal <= half) {
    // Symmetric case — chats needs less than its fair share.
    visibleChats = cTotal;
    visibleProjects = Math.min(pTotal, budget - cTotal);
  } else {
    // Both want more than half — split ~50/50, odd remainder to projects.
    visibleProjects = Math.min(pTotal, Math.ceil(budget / 2));
    visibleChats = Math.min(cTotal, budget - visibleProjects);
  }

  return { visibleProjects, visibleChats };
}
